import {
  type ApiResponse,
  type PaginatedResponse
} from '../../api-client';

import {
  apiRequest
} from './client';

import type {
  Customer,
  CustomerType,
  CreateCustomerInput,
  UpdateCustomerInput
} from './types/quotes.types';

import type {
  CustomerAddress,
  CreateCustomerAddressInput,
  UpdateCustomerAddressInput,
  PaymentMethodResource,
  CreatePaymentMethodInput,
  UpdatePaymentMethodInput
} from './types/customers.types';

export interface ListCustomersQueryParams {
  limit?: number
  offset?: number
}

export async function listAllCustomers(
  params: ListCustomersQueryParams = {},
  token: string
): Promise<PaginatedResponse<Customer>> {

  const query = new URLSearchParams();

  if (params.limit !== undefined) {

    query.set('limit', String(params.limit));
  }

  if (params.offset !== undefined) {

    query.set('offset', String(params.offset));
  }

  const qs = query.size > 0 ? `?${ query.toString() }` : '';
  return apiRequest<PaginatedResponse<Customer>>(`/customers${ qs }`, undefined, token);
}

export async function getCustomerById(
  id: number,
  token: string
): Promise<ApiResponse<Customer>> {

  return apiRequest<ApiResponse<Customer>>(`/customers/${ id }`, undefined, token);
}

export async function createCustomerFull(
  input: CreateCustomerInput,
  token: string
): Promise<ApiResponse<Customer>> {

  return apiRequest<ApiResponse<Customer>>(
    '/customers',
    {
      method: 'POST',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function updateCustomer(
  id: number,
  input: UpdateCustomerInput,
  token: string
): Promise<ApiResponse<Customer>> {

  return apiRequest<ApiResponse<Customer>>(
    `/customers/${ id }`,
    {
      method: 'PUT',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function deleteCustomer(
  id: number,
  token: string
): Promise<ApiResponse<void>> {

  return apiRequest<ApiResponse<void>>(
    `/customers/${ id }`,
    {
      method: 'DELETE'
    },
    token
  );
}

// ── Addresses ────────────────────────────────────────────────────────────────

export async function listCustomerAddresses(
  customerId: number,
  token: string
): Promise<PaginatedResponse<CustomerAddress>> {

  return apiRequest<PaginatedResponse<CustomerAddress>>(`/customers/${ customerId }/addresses`, undefined, token);
}

export async function createCustomerAddress(
  customerId: number,
  input: CreateCustomerAddressInput,
  token: string
): Promise<ApiResponse<CustomerAddress>> {

  return apiRequest<ApiResponse<CustomerAddress>>(
    `/customers/${ customerId }/addresses`,
    {
      method: 'POST',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function updateCustomerAddress(
  customerId: number,
  addressId: number,
  input: UpdateCustomerAddressInput,
  token: string
): Promise<ApiResponse<CustomerAddress>> {

  return apiRequest<ApiResponse<CustomerAddress>>(
    `/customers/${ customerId }/addresses/${ addressId }`,
    {
      method: 'PUT',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function deleteCustomerAddress(
  customerId: number,
  addressId: number,
  token: string
): Promise<ApiResponse<void>> {

  return apiRequest<ApiResponse<void>>(
    `/customers/${ customerId }/addresses/${ addressId }`,
    {
      method: 'DELETE'
    },
    token
  );
}

// ── Payment Methods ───────────────────────────────────────────────────────────

export async function listCustomerPaymentMethods(
  customerId: number,
  token: string
): Promise<PaginatedResponse<PaymentMethodResource>> {

  return apiRequest<PaginatedResponse<PaymentMethodResource>>(`/customers/${ customerId }/payment-methods`, undefined, token);
}

export async function createCustomerPaymentMethod(
  customerId: number,
  input: CreatePaymentMethodInput,
  token: string
): Promise<ApiResponse<PaymentMethodResource>> {

  return apiRequest<ApiResponse<PaymentMethodResource>>(
    `/customers/${ customerId }/payment-methods`,
    {
      method: 'POST',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function updateCustomerPaymentMethod(
  customerId: number,
  paymentMethodId: number,
  input: UpdatePaymentMethodInput,
  token: string
): Promise<ApiResponse<PaymentMethodResource>> {

  return apiRequest<ApiResponse<PaymentMethodResource>>(
    `/customers/${ customerId }/payment-methods/${ paymentMethodId }`,
    {
      method: 'PUT',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function deleteCustomerPaymentMethod(
  customerId: number,
  paymentMethodId: number,
  token: string
): Promise<ApiResponse<void>> {

  return apiRequest<ApiResponse<void>>(
    `/customers/${ customerId }/payment-methods/${ paymentMethodId }`,
    {
      method: 'DELETE'
    },
    token
  );
}

// Re-export types so consumers can import from the service barrel
export type {
  CustomerType,
  Customer,
  CreateCustomerInput,
  UpdateCustomerInput,
  CustomerAddress,
  CreateCustomerAddressInput,
  UpdateCustomerAddressInput,
  PaymentMethodResource,
  CreatePaymentMethodInput,
  UpdatePaymentMethodInput
};
